import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  Download, BookOpen, FileText, BookMarked, Paperclip,
  ChevronRight, RefreshCw, X, Loader2, AlignLeft,
} from 'lucide-react'
import api from '../../lib/api'
import { supabase } from '../../lib/supabase'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function sectionCount(chapter) {
  const secs = chapter.toc_sections?.sections || []
  const subsTotal = secs.reduce((sum, s) => sum + (s.subsections?.length || 0), 0)
  return secs.length + subsTotal
}

// ─── Status config ─────────────────────────────────────────────────────────────

const STATUS = {
  NOT_STARTED: { label: 'Not Started', color: '#94a3b8',  border: '#e2e8f0',  bg: 'rgba(226,232,240,0.4)'  },
  DRAFT:       { label: 'Draft',       color: '#2563eb',  border: '#2563eb',  bg: 'rgba(37,99,235,0.08)'  },
  GENERATING:  { label: 'Generating',  color: '#2563eb',  border: '#2563eb',  bg: 'rgba(37,99,235,0.05)', pulse: true },
  REVIEWED:    { label: 'Reviewed',    color: '#16a34a',  border: '#16a34a',  bg: 'rgba(22,163,74,0.08)'  },
  LOCKED:      { label: 'Locked',      color: '#7c3aed',  border: '#7c3aed',  bg: 'rgba(124,58,237,0.08)' },
}

// ─── Progress ring ─────────────────────────────────────────────────────────────

function ProgressRing({ pct, size = 48 }) {
  const r   = (size - 6) / 2
  const circ = 2 * Math.PI * r
  const dash = circ * Math.min(pct / 100, 1)
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e2e8f0" strokeWidth={5} />
      <circle
        cx={size / 2} cy={size / 2} r={r}
        fill="none"
        stroke={pct >= 100 ? '#16a34a' : '#2563eb'}
        strokeWidth={5}
        strokeDasharray={`${dash} ${circ}`}
        strokeLinecap="round"
        style={{ transition: 'stroke-dasharray 0.6s ease' }}
      />
    </svg>
  )
}

// ─── Generation Slide-over ─────────────────────────────────────────────────────

function GenerateSlideOver({ chapter, onClose, onGenerated }) {
  const { id } = useParams()
  const navigate = useNavigate()
  const [notes,        setNotes]        = useState(chapter?.user_notes || '')
  const [placeholders, setPlaceholders] = useState(true)
  const [generating,   setGenerating]   = useState(false)
  const [error,        setError]        = useState('')

  if (!chapter) return null

  const tocSections = chapter.toc_sections?.sections || chapter.toc_sections || []

  const handleGenerate = async () => {
    setGenerating(true)
    setError('')
    try {
      // Save user notes to the chapter before navigating — the generator reads them from DB
      const fullNotes = notes + (placeholders
        ? '\n\nInsert [PLACEHOLDER] markers where primary data, statistics, or original survey results would be inserted by the candidate.'
        : '')

      await api.put(`/thesis/${id}/chapters/${chapter.id}`, { user_notes: fullNotes })

      onClose()
      onGenerated(chapter.id, 'GENERATING')
      // ChapterEditorPage will detect ?streaming=1 and call the generate endpoint itself
      navigate(`/app/thesis/${id}/chapters/${chapter.id}?streaming=1`)
    } catch (err) {
      setError(err.message || 'Generation failed')
      setGenerating(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-40"
      style={{ background: 'rgba(0,0,0,0.3)' }}
      onClick={onClose}
    >
      <div
        className="absolute right-0 top-0 h-full flex flex-col overflow-hidden"
        style={{ width: 480, background: '#FFFFFF', borderLeft: '1px solid #DDE4EF' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-5" style={{ borderBottom: '1px solid #DDE4EF' }}>
          <div>
            <p className="font-mono text-xs text-acdyon-textFaint mb-0.5">Chapter {chapter.chapter_number}</p>
            <h3 className="font-display text-xl text-acdyon-text leading-tight">{chapter.title}</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-acdyon-surface rounded transition-colors mt-1">
            <X style={{ width: 16, height: 16, color: '#8EA4BC' }} />
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">

          {/* Section tree */}
          <div>
            <p className="font-sans text-xs font-semibold text-acdyon-textFaint uppercase tracking-wide mb-3">
              Chapter Structure
            </p>
            {tocSections.length === 0 ? (
              <p className="font-sans text-xs text-acdyon-textFaint">No sections defined in ToC.</p>
            ) : (
              <div className="space-y-1">
                {tocSections.map(sec => (
                  <div key={sec.id}>
                    <div className="flex items-center gap-2 py-1">
                      <span className="font-mono text-xs shrink-0" style={{ color: '#2563eb', minWidth: 28 }}>{sec.number}</span>
                      <span className="font-sans text-sm text-acdyon-textMuted">{sec.title}</span>
                      <span className="font-mono text-xs text-acdyon-textFaint ml-auto shrink-0">{sec.pages}p</span>
                    </div>
                    {sec.subsections?.map(sub => (
                      <div key={sub.id} className="flex items-center gap-2 py-0.5 ml-6">
                        <span className="font-mono text-xs shrink-0" style={{ color: '#8EA4BC', minWidth: 28 }}>{sub.number}</span>
                        <span className="font-sans text-xs text-acdyon-textFaint">{sub.title}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="font-sans text-sm font-medium text-acdyon-text block mb-2">
              Pre-generation Notes
            </label>
            <p className="font-sans text-xs text-acdyon-textFaint mb-2">
              Any specific instructions, data points, or sources to include?
            </p>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={4}
              placeholder="e.g. Focus heavily on Indian context for Section 2.3. Include the TAM and UTAUT frameworks..."
              className="w-full px-3 py-2.5 rounded-lg font-sans text-sm text-acdyon-text placeholder:text-acdyon-textFaint outline-none resize-none"
              style={{ background: '#F4F7FC', border: '1px solid #DDE4EF' }}
              onFocus={e => (e.target.style.borderColor = 'rgba(37,99,235,0.4)')}
              onBlur={e => (e.target.style.borderColor = '#DDE4EF')}
            />
          </div>

          {/* Placeholders toggle */}
          <div className="flex items-center justify-between py-3 px-4 rounded-lg" style={{ background: '#F4F7FC', border: '1px solid #DDE4EF' }}>
            <div>
              <p className="font-sans text-sm font-medium text-acdyon-text">Include data placeholders</p>
              <p className="font-sans text-xs text-acdyon-textFaint mt-0.5">
                Inserts [PLACEHOLDER] markers where primary data would go
              </p>
            </div>
            <button
              type="button"
              onClick={() => setPlaceholders(v => !v)}
              className="w-10 h-6 rounded-full transition-colors relative shrink-0"
              style={{ background: placeholders ? '#2563eb' : '#2A4060' }}
            >
              <span
                className="absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform"
                style={{ left: placeholders ? 'calc(100% - 22px)' : '2px' }}
              />
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-lg text-center" style={{ background: '#F4F7FC' }}>
              <p className="font-mono text-base font-semibold text-brand-600">{chapter.estimated_pages}</p>
              <p className="font-sans text-xs text-acdyon-textFaint mt-0.5">Est. Pages</p>
            </div>
            <div className="p-3 rounded-lg text-center" style={{ background: '#F4F7FC' }}>
              <p className="font-mono text-base font-semibold text-brand-600">{(chapter.target_words || 0).toLocaleString()}</p>
              <p className="font-sans text-xs text-acdyon-textFaint mt-0.5">Target Words</p>
            </div>
          </div>

          {error && (
            <div className="px-4 py-3 rounded-lg font-sans text-sm" style={{ background: 'rgba(239,68,68,0.08)', color: '#F87171', border: '1px solid rgba(239,68,68,0.2)' }}>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4" style={{ borderTop: '1px solid #DDE4EF' }}>
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="w-full flex items-center justify-center gap-3 rounded-xl font-sans font-semibold text-sm transition-all disabled:opacity-60"
            style={{ background: '#2563eb', color: '#FFFFFF', height: 56 }}
          >
            {generating ? (
              <><Loader2 style={{ width: 16, height: 16 }} className="animate-spin" /> Connecting…</>
            ) : (
              <>Generate Full Chapter <ChevronRight style={{ width: 16, height: 16 }} /></>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Chapter Card ─────────────────────────────────────────────────────────────

function ChapterCard({ chapter, onGenerate }) {
  const navigate   = useNavigate()
  const { id }     = useParams()
  const cfg        = STATUS[chapter.status] || STATUS.NOT_STARTED
  const progress   = chapter.target_words > 0 ? Math.min((chapter.word_count / chapter.target_words) * 100, 100) : 0
  const sections   = sectionCount(chapter)

  return (
    <div
      className="relative rounded-xl overflow-hidden flex flex-col"
      style={{
        background:   '#FFFFFF',
        border:       `1px solid ${cfg.border}`,
        borderLeft:   `4px solid ${cfg.border}`,
        padding:      '24px',
        ...(cfg.pulse ? { animation: 'chapterPulse 2s ease-in-out infinite' } : {}),
      }}
    >
      {/* Watermark chapter number */}
      <span
        className="absolute top-3 right-4 font-display select-none pointer-events-none"
        style={{ fontSize: 72, lineHeight: 1, color: 'rgba(37,99,235,0.04)', fontWeight: 700 }}
      >
        {String(chapter.chapter_number).padStart(2, '0')}
      </span>

      {/* Top row */}
      <div className="flex items-center gap-2 mb-3 relative z-10">
        <span className="font-mono text-xs px-1.5 py-0.5 rounded" style={{ background: `${cfg.color}18`, color: cfg.color }}>
          CH {String(chapter.chapter_number).padStart(2, '0')}
        </span>
        <span className="font-sans text-xs px-2 py-0.5 rounded-full" style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}40` }}>
          {cfg.label}
        </span>
      </div>

      {/* Title */}
      <h3 className="font-sans font-semibold text-acdyon-text mb-1 relative z-10" style={{ fontSize: 18 }}>
        {chapter.title}
      </h3>
      <p className="font-mono text-xs text-acdyon-textFaint mb-4">{sections} sections · ~{chapter.estimated_pages} pages</p>

      {/* Progress bar */}
      <div className="mb-1">
        <div className="h-1.5 rounded-full overflow-hidden" style={{ background: '#DDE4EF' }}>
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{ width: `${progress}%`, background: progress >= 100 ? '#3DB87A' : '#2563eb' }}
          />
        </div>
        <p className="font-mono text-xs text-acdyon-textFaint mt-1.5">
          {(chapter.word_count || 0).toLocaleString()} / {(chapter.target_words || 0).toLocaleString()} words
        </p>
      </div>

      {/* Buttons */}
      <div className="flex items-center gap-2 mt-auto pt-4 relative z-10">
        {chapter.status === 'NOT_STARTED' && (
          <button
            onClick={() => onGenerate(chapter)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-sans text-sm font-semibold transition-all"
            style={{ background: '#2563eb', color: '#FFFFFF' }}
          >
            Generate Chapter <ChevronRight style={{ width: 14, height: 14 }} />
          </button>
        )}
        {chapter.status === 'DRAFT' && (
          <>
            <Link
              to={`/app/thesis/${id}/chapters/${chapter.id}`}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-sans text-sm font-medium transition-colors"
              style={{ border: '1px solid rgba(37,99,235,0.4)', color: '#2563eb' }}
            >
              Continue Editing
            </Link>
            <button
              onClick={() => onGenerate(chapter)}
              className="px-3 py-2 rounded-lg font-sans text-xs text-acdyon-textMuted hover:text-acdyon-text transition-colors"
              style={{ border: '1px solid #DDE4EF' }}
            >
              <RefreshCw style={{ width: 13, height: 13 }} />
            </button>
          </>
        )}
        {chapter.status === 'REVIEWED' && (
          <>
            <Link
              to={`/app/thesis/${id}/chapters/${chapter.id}`}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-sans text-sm font-medium transition-colors"
              style={{ border: '1px solid #DDE4EF', color: '#0C1629' }}
            >
              View Chapter
            </Link>
            <Link
              to={`/app/thesis/${id}/chapters/${chapter.id}?edit=1`}
              className="px-3 py-2 rounded-lg font-sans text-xs text-acdyon-textMuted hover:text-acdyon-text transition-colors"
              style={{ border: '1px solid #DDE4EF' }}
            >
              Edit
            </Link>
          </>
        )}
        {chapter.status === 'GENERATING' && (
          <div className="flex items-center gap-2 px-4 py-2 rounded-lg" style={{ border: '1px solid rgba(37,99,235,0.3)' }}>
            <Loader2 style={{ width: 14, height: 14, color: '#2563eb' }} className="animate-spin" />
            <span className="font-sans text-sm text-brand-600">Generating…</span>
          </div>
        )}
        {chapter.status === 'LOCKED' && (
          <Link
            to={`/app/thesis/${id}/chapters/${chapter.id}`}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-sans text-sm font-medium transition-colors"
            style={{ border: '1px solid #DDE4EF', color: '#0C1629' }}
          >
            View Chapter
          </Link>
        )}
      </div>
    </div>
  )
}

// ─── Special section card ─────────────────────────────────────────────────────

function SpecialCard({ icon: Icon, title, subtitle, buttonLabel, onClick, color = '#4A6080' }) {
  return (
    <div
      className="rounded-xl p-6 flex flex-col"
      style={{ background: '#F4F7FC', border: '1px solid #DDE4EF', borderLeft: `4px solid ${color}` }}
    >
      <div className="flex items-center gap-3 mb-2">
        <Icon style={{ width: 18, height: 18, color }} />
        <h3 className="font-sans font-semibold text-acdyon-text" style={{ fontSize: 16 }}>{title}</h3>
      </div>
      <p className="font-sans text-xs text-acdyon-textFaint mb-4">{subtitle}</p>
      <button
        onClick={onClick}
        className="mt-auto flex items-center gap-2 px-4 py-2 rounded-lg font-sans text-sm font-medium transition-colors self-start"
        style={{ border: `1px solid ${color}50`, color }}
      >
        {buttonLabel} <ChevronRight style={{ width: 13, height: 13 }} />
      </button>
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ChapterHubPage() {
  const { id } = useParams()

  const [thesis,   setThesis]   = useState(null)
  const [chapters, setChapters] = useState([])
  const [loading,  setLoading]  = useState(true)
  const [generating, setGenerating] = useState(null) // chapter being generated

  const load = async () => {
    try {
      const [thesisRes, chaptersRes] = await Promise.all([
        api.get(`/thesis/${id}`),
        api.get(`/thesis/${id}/chapters`),
      ])
      setThesis(thesisRes.data)
      setChapters(chaptersRes.data || [])
    } catch {
      // silent
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [id]) // eslint-disable-line react-hooks/exhaustive-deps

  // Poll while any chapter is GENERATING
  useEffect(() => {
    const anyGenerating = chapters.some(c => c.status === 'GENERATING')
    if (!anyGenerating) return
    const timer = setInterval(async () => {
      try {
        const { data } = await api.get(`/thesis/${id}/chapters`)
        setChapters(data || [])
      } catch { /* */ }
    }, 4000)
    return () => clearInterval(timer)
  }, [chapters, id])

  const handleChapterStatusUpdate = (chapterId, status) => {
    setChapters(prev => prev.map(c => c.id === chapterId ? { ...c, status } : c))
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 rounded-full border-2 border-brand-500 border-t-transparent animate-spin" />
      </div>
    )
  }

  const totalWords   = chapters.reduce((s, c) => s + (c.word_count || 0), 0)
  const totalPages   = Math.round(totalWords / 250)
  const chapDone     = chapters.filter(c => c.status === 'LOCKED' || c.status === 'REVIEWED').length
  const pct          = Math.min(Math.round((totalPages / 200) * 100), 100)

  return (
    <>
      {/* Chapter generation slide-over */}
      {generating && (
        <GenerateSlideOver
          chapter={generating}
          onClose={() => setGenerating(null)}
          onGenerated={handleChapterStatusUpdate}
        />
      )}

      {/* Pulse animation */}
      <style>{`
        @keyframes chapterPulse {
          0%, 100% { border-color: rgba(37,99,235,0.4); }
          50%       { border-color: rgba(37,99,235,1); }
        }
      `}</style>

      <div className="space-y-8 max-w-6xl">

        {/* ── Header ──────────────────────────────────────────────── */}
        <div className="flex items-start justify-between gap-6">
          <div className="min-w-0">
            <h1
              className="font-display text-acdyon-text"
              style={{ fontSize: '2.25rem', lineHeight: 1.15 }}
            >
              {thesis?.title || 'My Thesis'}
            </h1>
            {thesis && (
              <div className="flex flex-wrap items-center gap-2 mt-2">
                {[thesis.university, thesis.programme, thesis.methodology].filter(Boolean).map(v => (
                  <span
                    key={v}
                    className="font-mono text-xs px-2 py-0.5 rounded"
                    style={{ background: 'rgba(37,99,235,0.08)', color: '#2563eb', border: '1px solid rgba(37,99,235,0.2)' }}
                  >
                    {v}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <Link
              to={`/app/thesis/${id}/toc`}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-sans text-sm text-acdyon-textMuted hover:text-acdyon-text transition-colors"
              style={{ border: '1px solid #DDE4EF' }}
            >
              <BookOpen style={{ width: 14, height: 14 }} /> View ToC
            </Link>
            <button
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-sans text-sm text-acdyon-textMuted hover:text-acdyon-text transition-colors"
              style={{ border: '1px solid #DDE4EF' }}
            >
              <Download style={{ width: 14, height: 14 }} /> Export Full Thesis
            </button>
          </div>
        </div>

        {/* ── Stats bar ───────────────────────────────────────────── */}
        <div
          className="grid grid-cols-4 gap-0 rounded-xl overflow-hidden"
          style={{ background: '#F4F7FC', border: '1px solid #DDE4EF' }}
        >
          {[
            { label: 'Total Words',      value: totalWords.toLocaleString() },
            { label: 'Estimated Pages',  value: totalPages },
            { label: 'Chapters Complete', value: `${chapDone} / ${chapters.length}` },
          ].map(({ label, value }, i) => (
            <div
              key={label}
              className="flex flex-col items-center justify-center py-5"
              style={{ borderRight: '1px solid #DDE4EF' }}
            >
              <p className="font-mono text-2xl font-semibold text-brand-600">{value}</p>
              <p className="font-sans text-xs text-acdyon-textFaint mt-1">{label}</p>
            </div>
          ))}
          {/* Progress ring */}
          <div className="flex flex-col items-center justify-center py-5 gap-1">
            <ProgressRing pct={pct} size={52} />
            <p className="font-mono text-sm font-semibold" style={{ color: pct >= 100 ? '#3DB87A' : '#2563eb' }}>{pct}%</p>
            <p className="font-sans text-xs text-acdyon-textFaint">of 200 pages</p>
          </div>
        </div>

        {/* ── Chapter grid ─────────────────────────────────────────── */}
        <div>
          <h2 className="font-sans font-semibold text-acdyon-text mb-4">Chapters</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {chapters.map(chapter => (
              <ChapterCard
                key={chapter.id}
                chapter={chapter}
                onGenerate={setGenerating}
              />
            ))}
          </div>
        </div>

        {/* ── Special sections ─────────────────────────────────────── */}
        <div>
          <h2 className="font-sans font-semibold text-acdyon-text mb-4">Supporting Sections</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <SpecialCard
              icon={FileText}
              title="Front Matter"
              subtitle="Title page, abstract, acknowledgements, declaration"
              buttonLabel="Generate Front Matter"
              color="#4C9BC9"
            />
            <SpecialCard
              icon={BookMarked}
              title="References"
              subtitle="Manage your bibliography and citation library"
              buttonLabel="Manage References"
              color="#3DB87A"
            />
            <SpecialCard
              icon={Paperclip}
              title="Appendices"
              subtitle="Research instruments, data tables, supplementary material"
              buttonLabel="Add Appendices"
              color="#9B4CC9"
            />
          </div>
        </div>

      </div>
    </>
  )
}
